import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { TrendingUp, Lock, Users, AlertCircle, Award, Target, Check, ArrowRight, Share2, Sparkles, ChevronUp, ChevronDown, Minus } from 'lucide-react'
import DashboardLayout from '../components/DashboardLayout'
import LevelSelector, { categoryHasLevels, getLevelsForCategory, SENIOR_LEVEL_LABELS } from '../components/LevelSelector'
import ShareModal from '../components/ShareModal'
import { useAuth } from '../context/AuthContext'
import { estVraiFree } from '../utils/planAccess'
import { useSeason, formatSeasonShort } from '../context/SeasonContext'
import { useCategoriesOptions } from '../constants/categories'
import api from '../services/api'
import { T, globalStyles } from '../theme'

// La liste des catégories vient désormais du hook SSoT (BUG-012, ARCHI-002).
// Consommée via useCategoriesOptions() dans NoSegmentState ci-dessous.

// ── Sous-composants ─────────────────────────────────────────────────


function ComparisonRow({ label, mine, segment, top25, format = 'pct', sub, higherIsBetter = true }) {
  const fmt = (v) => {
    if (v == null || v === undefined) return '—'
    if (format === 'pct') return `${v}%`
    if (format === 'num') return v
    if (format === 'avg') return v.toFixed(2).replace('.', ',')
    return v
  }

  // Calcul direction vs moyenne segment
  let arrow = null
  if (mine != null && segment != null && typeof mine === 'number' && typeof segment === 'number') {
    const diff = mine - segment
    const threshold = format === 'pct' ? 2 : 0.1
    if (Math.abs(diff) < threshold) {
      arrow = <Minus size={12} color={T.muted} />
    } else {
      const isPositive = higherIsBetter ? diff > 0 : diff < 0
      arrow = isPositive
        ? <ChevronUp size={12} color="#22c55e" />
        : <ChevronDown size={12} color="#ef4444" />
    }
  }

  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: '1.5fr 1fr 1fr 1fr',
      alignItems: 'center', gap: 8,
      padding: '14px 18px',
      borderBottom: `1px solid ${T.rule}`,
    }}>
      <div>
        <div style={{ fontFamily: T.mono, fontSize: 10, letterSpacing: '.04em', color: T.ink, fontWeight: 600 }}>
          {label}
        </div>
        {sub && <div style={{ fontFamily: T.mono, fontSize: 8, color: T.muted, letterSpacing: '.06em', marginTop: 2 }}>{sub}</div>}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'center' }}>
        <span style={{ fontFamily: T.display, fontSize: 22, fontWeight: 800, color: T.ink, lineHeight: 1 }}>{fmt(mine)}</span>
        {arrow}
      </div>
      <div style={{ textAlign: 'center' }}>
        <span style={{ fontFamily: T.display, fontSize: 18, color: T.ink, opacity: 0.6, lineHeight: 1 }}>{fmt(segment)}</span>
      </div>
      <div style={{ textAlign: 'center' }}>
        <span style={{ fontFamily: T.display, fontSize: 18, color: '#22c55e', opacity: 0.85, lineHeight: 1 }}>{fmt(top25)}</span>
      </div>
    </div>
  )
}


/** ─── État FREE — upgrade requis ─── */
function FreeLockedState({ isMobile }) {
  return (
    <div style={{
      background: T.surface, border: `0.5px solid ${T.rule}`,
      borderTop: `2.5px solid ${T.dark}`, borderRadius: 3,
      padding: isMobile ? '40px 24px' : '48px 40px',
    }}>
      <div style={{ maxWidth: 480 }}>
        <div style={{
          width: 48, height: 48, background: T.bgAlt, border: `0.5px solid ${T.rule}`,
          borderRadius: 3,
          display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 20,
        }}>
          <Lock size={22} color={T.muted} />
        </div>
        <p style={{ fontFamily: T.mono, fontSize: 9, fontWeight: 600, letterSpacing: '.15em', textTransform: 'uppercase', color: T.muted, marginBottom: 12 }}>
          Comparatif national
        </p>
        <h2 style={{ fontFamily: T.display, fontSize: isMobile ? 28 : 36, fontWeight: 800, textTransform: 'uppercase', lineHeight: .92, color: T.ink, marginBottom: 16 }}>
          Compare ton équipe aux clubs de ton niveau.
        </h2>
        <p style={{ fontFamily: T.mono, fontSize: 11, color: T.muted, lineHeight: 1.7, marginBottom: 24 }}>
          Buts marqués, V/N/D %, clean sheets, formations dominantes.
          Vois où tu te situes par rapport aux autres clubs de ta catégorie et de ton niveau.
        </p>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <Link to="/dashboard/settings" style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            padding: '0 22px', minHeight: 44,
            background: T.dark, color: '#FFFFFF',
            fontFamily: T.mono, fontSize: 11, fontWeight: 600,
            textDecoration: 'none', borderRadius: 3,
          }}>
            Passer en Coach+ <ArrowRight size={13} />
          </Link>
          <Link to="/dashboard/settings" style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            padding: '0 22px', minHeight: 44,
            background: 'transparent', color: T.ink,
            fontFamily: T.mono, fontSize: 11,
            border: `0.5px solid ${T.rule}`, textDecoration: 'none', borderRadius: 3,
          }}>
            Voir les plans
          </Link>
        </div>
      </div>
    </div>
  )
}


/** ─── État NO SEGMENT — formulaire d'activation inline ─── */
function NoSegmentState({ isMobile, onSubmit, submitting, error }) {
  const { categories } = useCategoriesOptions()
  // Default explicite 'Seniors' (cf. ajustement brief) : niveau de référence
  // pour le benchmark, max d'historique. PAS categories[0] — robuste si la
  // SSoT évolue (un futur 'U13' en tête ne deviendrait pas le défaut silencieux).
  const [category, setCategory] = useState('')
  const [level, setLevel] = useState(null)

  useEffect(() => {
    if (category) return
    if (categories.includes('Seniors')) {
      setCategory('Seniors')
    }
  }, [categories, category])

  const needsLevel = categoryHasLevels(category)
  const canSubmit = !!category && (!needsLevel || !!level)

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Header explication */}
      <div style={{ background: T.surface, border: `0.5px solid ${T.rule}`, borderTop: `2px solid ${T.dark}`, borderRadius: 3, padding: isMobile ? '24px 20px' : '28px 32px' }}>
        <div style={{ fontFamily: T.mono, fontSize: 9, fontWeight: 600, letterSpacing: '.15em', textTransform: 'uppercase', color: T.muted, marginBottom: 12 }}>
          Activer mon comparatif
        </div>
        <h2 style={{ fontFamily: T.display, fontSize: isMobile ? 24 : 32, fontWeight: 800, textTransform: 'uppercase', lineHeight: .92, color: T.ink, marginBottom: 14 }}>
          Renseigne ton niveau de championnat.
        </h2>
        <p style={{ fontFamily: T.mono, fontSize: 11, color: T.muted, lineHeight: 1.7, margin: 0, maxWidth: 560 }}>
          Indique la catégorie que tu entraînes et ton niveau de championnat.
          Ton comparatif s'activera automatiquement dès que d'autres clubs du même segment seront actifs.
        </p>
      </div>

      {/* Formulaire */}
      <div style={{ background: T.surface, border: `0.5px solid ${T.rule}`, borderRadius: 3, padding: isMobile ? '24px 20px' : '28px 32px' }}>
        <div style={{ marginBottom: 24 }}>
          <label style={{ display: 'block', fontFamily: T.mono, fontSize: 9, fontWeight: 600, letterSpacing: '.12em', textTransform: 'uppercase', color: T.muted, marginBottom: 8 }}>
            Catégorie entraînée
          </label>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))', gap: 6 }}>
            {categories.map(cat => (
              <button
                key={cat}
                type="button"
                onClick={() => { setCategory(cat); setLevel(null) }}
                style={{
                  padding: '10px 8px', minHeight: 44,
                  background: category === cat ? T.dark : T.surface,
                  border: `0.5px solid ${category === cat ? T.dark : T.rule}`,
                  color: category === cat ? '#FFFFFF' : T.muted,
                  fontFamily: T.mono, fontSize: 10, fontWeight: category === cat ? 600 : 400,
                  cursor: 'pointer', borderRadius: 3, transition: 'all .12s',
                }}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>

        <div style={{ marginBottom: 24 }}>
          <LevelSelector
            category={category}
            value={level}
            onChange={setLevel}
            required={needsLevel}
            hint={false}
          />
        </div>

        {error && (
          <div style={{ marginBottom: 16, padding: '10px 14px', background: T.redBg, border: `0.5px solid ${T.redBdr}`, borderRadius: 3, display: 'flex', alignItems: 'center', gap: 8 }}>
            <AlertCircle size={14} color={T.red} />
            <span style={{ fontFamily: T.mono, fontSize: 10, color: T.red }}>{error}</span>
          </div>
        )}

        <button
          onClick={() => canSubmit && onSubmit(category, level)}
          disabled={!canSubmit || submitting}
          style={{
            padding: '0 24px', minHeight: 44,
            background: canSubmit ? T.dark : T.rule,
            color: canSubmit ? '#FFFFFF' : T.muted,
            fontFamily: T.mono, fontSize: 11, fontWeight: 600,
            border: 'none', cursor: canSubmit && !submitting ? 'pointer' : 'not-allowed',
            display: 'inline-flex', alignItems: 'center', gap: 8, borderRadius: 3,
          }}>
          {submitting ? 'Enregistrement…' : <>Activer mon comparatif <ArrowRight size={13} /></>}
        </button>
      </div>
    </div>
  )
}


/** ─── État NOT ENOUGH CLUBS — progression vers déblocage ─── */
function NotEnoughClubsState({ data, isMobile, onShareClick }) {
  const { segment, eligible_count, threshold, my_stats, my_stats_unlocked } = data
  const segmentLabel = `${segment.category} ${SENIOR_LEVEL_LABELS[segment.level] ?? segment.level}`
  const progressPct = Math.min(100, Math.round((eligible_count / threshold) * 100))

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

      {/* Bloc déverrouillage progression */}
      <div style={{
        background: T.surface, border: `0.5px solid ${T.rule}`,
        borderTop: `2.5px solid ${T.accentStat2}`, borderRadius: 3,
        padding: isMobile ? '28px 20px' : '36px 40px',
      }}>
        <div>
          <p style={{ fontFamily: T.mono, fontSize: 9, fontWeight: 600, letterSpacing: '.15em', textTransform: 'uppercase', color: T.muted, marginBottom: 14 }}>
            {segmentLabel}
          </p>

          <h2 style={{ fontFamily: T.display, fontSize: isMobile ? 26 : 36, fontWeight: 800, textTransform: 'uppercase', lineHeight: .92, color: T.ink, marginBottom: 20 }}>
            Comparatif en construction.
          </h2>

          {/* Progress numérique */}
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 12 }}>
            <span style={{ fontFamily: T.display, fontSize: isMobile ? 48 : 60, fontWeight: 800, color: T.accentStat2, lineHeight: 1 }}>{eligible_count}</span>
            <span style={{ fontFamily: T.mono, fontSize: 13, color: T.muted }}>
              / {threshold} clubs minimum
            </span>
          </div>

          {/* Progress bar */}
          <div style={{ height: 4, background: T.rule, marginBottom: 20, borderRadius: 2 }}>
            <div style={{
              height: '100%', width: `${progressPct}%`,
              background: T.accentStat2, borderRadius: 2,
              transition: 'width .8s ease',
            }} />
          </div>

          <p style={{ fontFamily: T.mono, fontSize: 11, color: T.muted, lineHeight: 1.7, marginBottom: 20, maxWidth: 520 }}>
            Le comparatif {segmentLabel} s'active dès que <strong style={{ color: T.ink }}>{threshold} clubs de ton niveau</strong> utilisent Insightball.
            Plus on est nombreux, plus la comparaison devient pertinente.
          </p>

          <button
            onClick={onShareClick}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              padding: '0 22px', minHeight: 44,
              background: T.dark, color: '#FFFFFF',
              fontFamily: T.mono, fontSize: 11, fontWeight: 600,
              border: 'none', cursor: 'pointer', borderRadius: 3,
            }}>
            <Share2 size={13} /> Partager Insightball
          </button>
        </div>
      </div>

      {/* Bloc "tes stats à toi" — toujours visible si on a assez de matchs */}
      {my_stats_unlocked && my_stats?.matches_count > 0 && (
        <div style={{ background: T.surface, border: `0.5px solid ${T.rule}`, borderTop: `2px solid ${T.accentStat4}`, borderRadius: 3 }}>
          <div style={{ padding: '14px 20px', borderBottom: `0.5px solid ${T.rule}`, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Target size={14} color={T.accentStat4} />
            <span style={{ fontFamily: T.mono, fontSize: 9, fontWeight: 600, letterSpacing: '.14em', textTransform: 'uppercase', color: T.muted }}>
              En attendant — tes stats {segmentLabel}
            </span>
            <span style={{ fontFamily: T.mono, fontSize: 9, color: T.muted, marginLeft: 'auto' }}>
              {my_stats.matches_count} matchs
            </span>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2,1fr)' : 'repeat(4,1fr)', gap: 8, padding: '16px' }}>
            {[
              { label: 'Victoires', value: `${my_stats.wins_pct}%`, accent: T.accentStat4 },
              { label: 'Buts marqués', value: my_stats.goals_scored_avg.toFixed(2).replace('.', ','), sub: '/ match', accent: T.accentStat2 },
              { label: 'Buts encaissés', value: my_stats.goals_conceded_avg.toFixed(2).replace('.', ','), sub: '/ match', accent: T.accentStat3 },
              { label: 'Clean sheets', value: `${my_stats.clean_sheets_pct}%`, accent: T.accentStat1 },
            ].map(k => (
              <div key={k.label} style={{ background: T.bgAlt, border: `0.5px solid ${T.rule}`, borderTop: `2px solid ${k.accent}`, borderRadius: 3, padding: '14px 14px' }}>
                <p style={{ fontFamily: T.mono, fontSize: 9, fontWeight: 600, letterSpacing: '.12em', textTransform: 'uppercase', color: T.muted, marginBottom: 6 }}>{k.label}</p>
                <p style={{ fontFamily: T.display, fontSize: 26, fontWeight: 800, lineHeight: 1, color: T.ink, margin: 0 }}>{k.value}</p>
                {k.sub && <p style={{ fontFamily: T.mono, fontSize: 8, color: T.muted, marginTop: 4 }}>{k.sub}</p>}
              </div>
            ))}
          </div>

          {my_stats.top_formations?.length > 0 && (
            <div style={{ padding: '16px 20px', borderTop: `1px solid ${T.rule}` }}>
              <div style={{ fontFamily: T.mono, fontSize: 8, letterSpacing: '.16em', textTransform: 'uppercase', color: T.muted, marginBottom: 10 }}>
                Tes formations
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {my_stats.top_formations.map((f, i) => (
                  <span key={f.formation} style={{
                    fontFamily: T.mono, fontSize: 10, letterSpacing: '.06em',
                    padding: '6px 10px', background: i === 0 ? T.goldBg : T.bg,
                    color: i === 0 ? T.gold : T.ink,
                    border: `1px solid ${i === 0 ? T.goldBdr : T.rule}`,
                  }}>
                    {f.formation} <span style={{ opacity: 0.6, marginLeft: 4 }}>{f.pct}%</span>
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Pas assez de matchs côté coach */}
      {!my_stats_unlocked && (
        <div style={{ background: T.surface, border: `1px solid ${T.rule}`, padding: '20px 24px' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
            <AlertCircle size={18} color={T.muted} style={{ flexShrink: 0, marginTop: 2 }} />
            <div>
              <p style={{ fontFamily: T.mono, fontSize: 11, color: T.ink, lineHeight: 1.6, margin: 0 }}>
                <strong>{my_stats?.matches_count || 0} match{(my_stats?.matches_count || 0) > 1 ? 's' : ''} renseigné{(my_stats?.matches_count || 0) > 1 ? 's' : ''} en championnat.</strong>
                {' '}Il en faut au moins 5 pour que tes propres stats soient calculées.
              </p>
              <Link to="/dashboard/matches/new" style={{
                display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 10,
                fontFamily: T.mono, fontSize: 9, letterSpacing: '.12em', textTransform: 'uppercase',
                color: T.gold, textDecoration: 'none',
              }}>
                Saisir un match → 
              </Link>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}


/** ─── État UNLOCKED — comparatif complet ─── */
function UnlockedState({ data, isMobile, onShareClick }) {
  const { segment, eligible_count, my_stats, my_stats_unlocked, segment_stats, top25_stats } = data
  const segmentLabel = `${segment.category} ${SENIOR_LEVEL_LABELS[segment.level] ?? segment.level}`

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

      {/* Header — segment activé */}
      <div style={{
        background: T.surface, border: `0.5px solid ${T.rule}`,
        borderTop: `2.5px solid ${T.accentStat4}`, borderRadius: 3,
        padding: isMobile ? '24px 20px' : '28px 32px',
      }}>
        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
          <div>
            <p style={{ fontFamily: T.mono, fontSize: 9, fontWeight: 600, letterSpacing: '.15em', textTransform: 'uppercase', color: T.accentStat4, marginBottom: 10 }}>
              Comparatif activé
            </p>
            <h2 style={{ fontFamily: T.display, fontSize: isMobile ? 26 : 32, fontWeight: 800, textTransform: 'uppercase', lineHeight: .92, color: T.ink, margin: 0 }}>
              {segment.category} {SENIOR_LEVEL_LABELS[segment.level] ?? segment.level}.
            </h2>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontFamily: T.display, fontSize: 36, fontWeight: 800, color: T.accentStat4, lineHeight: 1 }}>{eligible_count}</div>
            <div style={{ fontFamily: T.mono, fontSize: 8, fontWeight: 600, letterSpacing: '.12em', textTransform: 'uppercase', color: T.muted }}>clubs analysés</div>
          </div>
        </div>
      </div>

      {/* Pas assez de matchs côté coach pour comparer */}
      {!my_stats_unlocked && (
        <div style={{ background: '#fff7e6', border: '1px solid rgba(217,119,6,0.3)', padding: '16px 20px', display: 'flex', alignItems: 'flex-start', gap: 12 }}>
          <AlertCircle size={16} color="#d97706" style={{ flexShrink: 0, marginTop: 2 }} />
          <div>
            <p style={{ fontFamily: T.mono, fontSize: 11, color: T.ink, lineHeight: 1.6, margin: 0 }}>
              Tu n'as que <strong>{my_stats?.matches_count || 0} match{(my_stats?.matches_count || 0) > 1 ? 's' : ''}</strong> renseigné{(my_stats?.matches_count || 0) > 1 ? 's' : ''} en championnat sur cette saison. Il en faut au moins 5 pour comparer tes stats.
            </p>
          </div>
        </div>
      )}

      {/* Tableau comparatif */}
      <div style={{ background: T.surface, border: `1px solid ${T.rule}` }}>
        {/* Header colonnes */}
        <div style={{
          display: 'grid', gridTemplateColumns: '1.5fr 1fr 1fr 1fr',
          padding: '14px 18px', borderBottom: `2px solid ${T.rule}`, background: T.bg,
          alignItems: 'center', gap: 8,
        }}>
          <div style={{ fontFamily: T.mono, fontSize: 9, letterSpacing: '.16em', textTransform: 'uppercase', color: T.muted }}>
            Indicateur
          </div>
          <div style={{ textAlign: 'center', fontFamily: T.mono, fontSize: 9, fontWeight: 700, letterSpacing: '.14em', textTransform: 'uppercase', color: T.ink }}>
            Toi
          </div>
          <div style={{ textAlign: 'center', fontFamily: T.mono, fontSize: 9, letterSpacing: '.14em', textTransform: 'uppercase', color: T.muted }}>
            Moyenne {SENIOR_LEVEL_LABELS[segment.level] ?? segment.level}
          </div>
          <div style={{ textAlign: 'center', fontFamily: T.mono, fontSize: 9, letterSpacing: '.14em', textTransform: 'uppercase', color: '#22c55e' }}>
            <Award size={11} style={{ display: 'inline', marginRight: 4, verticalAlign: '-1px' }} />
            Top 25%
          </div>
        </div>

        {/* Lignes */}
        <ComparisonRow
          label="Victoires"
          mine={my_stats_unlocked ? my_stats?.wins_pct : null}
          segment={segment_stats?.wins_pct}
          top25={top25_stats?.wins_pct}
          format="pct"
          higherIsBetter={true}
        />
        <ComparisonRow
          label="Nuls"
          mine={my_stats_unlocked ? my_stats?.draws_pct : null}
          segment={segment_stats?.draws_pct}
          top25={top25_stats?.draws_pct}
          format="pct"
          higherIsBetter={false}
        />
        <ComparisonRow
          label="Défaites"
          mine={my_stats_unlocked ? my_stats?.losses_pct : null}
          segment={segment_stats?.losses_pct}
          top25={top25_stats?.losses_pct}
          format="pct"
          higherIsBetter={false}
        />
        <ComparisonRow
          label="Buts marqués"
          sub="Moyenne par match"
          mine={my_stats_unlocked ? my_stats?.goals_scored_avg : null}
          segment={segment_stats?.goals_scored_avg}
          top25={top25_stats?.goals_scored_avg}
          format="avg"
          higherIsBetter={true}
        />
        <ComparisonRow
          label="Buts encaissés"
          sub="Moyenne par match"
          mine={my_stats_unlocked ? my_stats?.goals_conceded_avg : null}
          segment={segment_stats?.goals_conceded_avg}
          top25={top25_stats?.goals_conceded_avg}
          format="avg"
          higherIsBetter={false}
        />
        <ComparisonRow
          label="Clean sheets"
          sub="Matchs sans encaisser"
          mine={my_stats_unlocked ? my_stats?.clean_sheets_pct : null}
          segment={segment_stats?.clean_sheets_pct}
          top25={top25_stats?.clean_sheets_pct}
          format="pct"
          higherIsBetter={true}
        />
        <ComparisonRow
          label="Score moyen"
          sub="Buts totaux par match"
          mine={my_stats_unlocked ? my_stats?.score_avg_total : null}
          segment={segment_stats?.score_avg_total}
          top25={top25_stats?.score_avg_total}
          format="avg"
          higherIsBetter={false}
        />
      </div>

      {/* Top formations */}
      {(segment_stats?.top_formations?.length > 0 || my_stats?.top_formations?.length > 0) && (
        <div style={{ background: T.surface, border: `1px solid ${T.rule}`, padding: '20px 24px' }}>
          <div style={{ fontFamily: T.mono, fontSize: 9, fontWeight: 600, letterSpacing: '.14em', textTransform: 'uppercase', color: T.muted, marginBottom: 14 }}>
            Formations dominantes
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 20 }}>
            {/* Tes formations */}
            <div>
              <div style={{ fontFamily: T.mono, fontSize: 8, letterSpacing: '.14em', textTransform: 'uppercase', color: T.muted, marginBottom: 10 }}>
                Toi
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {(my_stats?.top_formations || []).map((f, i) => (
                  <span key={f.formation} style={{
                    fontFamily: T.mono, fontSize: 11, letterSpacing: '.06em',
                    padding: '6px 10px', borderRadius: 20,
                    background: i === 0 ? T.dark : T.bgAlt,
                    color: i === 0 ? '#FFFFFF' : T.ink,
                    border: `0.5px solid ${i === 0 ? T.dark : T.rule}`,
                  }}>
                    {f.formation} <span style={{ opacity: 0.6, marginLeft: 4 }}>{f.pct}%</span>
                  </span>
                ))}
                {(!my_stats?.top_formations || my_stats.top_formations.length === 0) && (
                  <span style={{ fontFamily: T.mono, fontSize: 10, color: T.muted }}>Pas encore de données</span>
                )}
              </div>
            </div>

            {/* Formations du segment */}
            <div>
              <div style={{ fontFamily: T.mono, fontSize: 8, letterSpacing: '.14em', textTransform: 'uppercase', color: T.muted, marginBottom: 10 }}>
                Segment {SENIOR_LEVEL_LABELS[segment.level] ?? segment.level}
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {(segment_stats?.top_formations || []).map((f, i) => (
                  <span key={f.formation} style={{
                    fontFamily: T.mono, fontSize: 11, letterSpacing: '.06em',
                    padding: '8px 12px',
                    background: T.bg, color: T.ink,
                    border: `1px solid ${T.rule}`,
                  }}>
                    {f.formation} <span style={{ opacity: 0.6, marginLeft: 4 }}>{f.pct}%</span>
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Footer — partager pour faire grandir le segment */}
      <div style={{ background: T.bgAlt, border: `0.5px solid ${T.rule}`, borderRadius: 3, padding: '14px 18px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <span style={{ fontFamily: T.mono, fontSize: 10, color: T.muted, lineHeight: 1.5 }}>
          Plus le segment grandit, plus la comparaison devient pertinente.
        </span>
        <button
          onClick={onShareClick}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '0 14px', minHeight: 36,
            background: 'transparent', color: T.ink,
            fontFamily: T.mono, fontSize: 9, fontWeight: 500,
            border: `0.5px solid ${T.rule}`, cursor: 'pointer', borderRadius: 3,
          }}>
          <Share2 size={11} /> Partager Insightball
        </button>
      </div>
    </div>
  )
}


// ── Page principale ────────────────────────────────────────────────


export default function Benchmark() {
  const { user } = useAuth()
  const { selectedSeason } = useSeason()
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [submittingSegment, setSubmittingSegment] = useState(false)
  const [submitError, setSubmitError] = useState(null)
  const [showShareModal, setShowShareModal] = useState(false)
  const [exploreCategory, setExploreCategory] = useState(null)
  const [exploreLevel, setExploreLevel] = useState(null)
  const [declaredCategory, setDeclaredCategory] = useState(null)
  const [declaredLevel, setDeclaredLevel] = useState(null)
  const [isEditingSegment, setIsEditingSegment] = useState(false)
  const [editCategory, setEditCategory] = useState(null)
  const [editLevel, setEditLevel] = useState(null)
  const [submittingEdit, setSubmittingEdit] = useState(false)
  const [editError, setEditError] = useState(null)
  const { categories } = useCategoriesOptions()
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < 768)

  // A6 · un membre de club n'est pas un compte d'essai · sa page Benchmark
  // etait entierement verrouillee, fetch coupe compris, alors que son club paie.
  const isFree = estVraiFree(user)

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768)
    window.addEventListener('resize', check)
    return () => window.removeEventListener('resize', check)
  }, [])

  // Charger le benchmark à chaque changement de saison (uniquement si plan payant)
  useEffect(() => {
    if (isFree) {
      setLoading(false)
      return
    }
    setExploreCategory(null)
    setExploreLevel(null)
    setDeclaredCategory(null)
    setDeclaredLevel(null)
    loadBenchmark()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSeason, isFree])

  const loadBenchmark = async (cat = null, lvl = null) => {
    setLoading(true)
    setError(null)
    try {
      const params = { season: selectedSeason }
      if (cat) params.category = cat
      if (lvl) params.level = lvl
      const { data } = await api.get('/benchmark/', { params })
      setData(data)
      if (!cat && !lvl && data.segment?.category) {
        setDeclaredCategory(data.segment.category)
        setDeclaredLevel(data.segment.level || null)
        setExploreCategory(data.segment.category)
        setExploreLevel(data.segment.level || null)
      }
    } catch (e) {
      setError('Impossible de charger le comparatif. Réessaie dans quelques instants.')
    } finally {
      setLoading(false)
    }
  }

  // Soumission du formulaire de segment (no_segment_declared)
  const handleSubmitSegment = async (category, level) => {
    setSubmittingSegment(true)
    setSubmitError(null)
    try {
      await api.post('/coach/club-history/', {
        season: selectedSeason,
        club_name: user?.club_name || user?.club?.name || '',
        category,
        level: level || null,
      })
      // Recharger le benchmark
      await loadBenchmark()
    } catch (e) {
      const detail = e?.response?.data?.detail
      setSubmitError(typeof detail === 'string' ? detail : 'Impossible d\'enregistrer ton segment. Réessaie.')
    } finally {
      setSubmittingSegment(false)
    }
  }

  const handleExploreCategory = (cat) => {
    setExploreCategory(cat)
    setExploreLevel(null)
    if (!categoryHasLevels(cat)) loadBenchmark(cat, null)
  }

  const handleExploreLevel = (lvl) => {
    setExploreLevel(lvl)
    if (exploreCategory && lvl) loadBenchmark(exploreCategory, lvl)
  }

  const handleResetExplore = () => {
    setExploreCategory(declaredCategory)
    setExploreLevel(declaredLevel)
    loadBenchmark()
  }

  const handleOpenEdit = () => {
    setEditCategory(declaredCategory)
    setEditLevel(declaredLevel)
    setEditError(null)
    setIsEditingSegment(true)
  }

  const handleSubmitEdit = async () => {
    const needsLvl = categoryHasLevels(editCategory)
    if (!editCategory || (needsLvl && !editLevel)) return
    setSubmittingEdit(true)
    setEditError(null)
    try {
      await api.post('/coach/club-history/', {
        season: selectedSeason,
        club_name: user?.club_name || user?.club?.name || '',
        category: editCategory,
        level: editLevel || null,
      })
      setIsEditingSegment(false)
      setExploreCategory(null)
      setExploreLevel(null)
      await loadBenchmark()
    } catch (e) {
      const detail = e?.response?.data?.detail
      setEditError(typeof detail === 'string' ? detail : "Impossible d'enregistrer. Réessaie.")
    } finally {
      setSubmittingEdit(false)
    }
  }

  const isExploring = !!(declaredCategory && data?.segment && (
    data.segment.category !== declaredCategory || data.segment.level !== declaredLevel
  ))
  const editNeedsLevel = categoryHasLevels(editCategory)
  const canEditSubmit = !!editCategory && (!editNeedsLevel || !!editLevel)

  // ── Rendu ──

  return (
    <DashboardLayout>
      <style>{globalStyles}</style>

      {/* HEADER */}
      <div style={{ marginBottom: 28, paddingBottom: 20, borderBottom: `0.5px solid ${T.rule}` }}>
        <p style={{ fontFamily: T.mono, fontSize: 9, fontWeight: 600, letterSpacing: '.15em', textTransform: 'uppercase', color: T.muted, marginBottom: 8 }}>
          Comparatif National · Saison {formatSeasonShort(selectedSeason)}
        </p>
        <h1 style={{ fontFamily: T.display, fontSize: 'clamp(28px, 5vw, 36px)', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '-.01em', lineHeight: 1, margin: 0, color: T.ink }}>
          Compare tes performances.
        </h1>
      </div>

      {/* ═══ Sélecteur d'exploration — affiché si segment déclaré ═══ */}
      {!isFree && !loading && data && data.reason !== 'no_segment_declared' && (
        <div style={{ marginBottom: 20, background: T.surface, border: `0.5px solid ${T.rule}`, borderRadius: 3, padding: isMobile ? '16px' : '20px 24px' }}>
          <div style={{ fontFamily: T.mono, fontSize: 9, fontWeight: 600, letterSpacing: '.14em', textTransform: 'uppercase', color: T.muted, marginBottom: 14 }}>
            Explorer un segment
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 20 }}>
            {/* Catégorie */}
            <div style={{ flex: '1 1 200px' }}>
              <div style={{ fontFamily: T.mono, fontSize: 8, letterSpacing: '.14em', textTransform: 'uppercase', color: T.muted, marginBottom: 8 }}>
                Catégorie
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {categories.map(cat => (
                  <button
                    key={cat}
                    type="button"
                    onClick={() => handleExploreCategory(cat)}
                    style={{
                      padding: '8px 10px',
                      background: exploreCategory === cat ? T.dark : T.surface,
                      border: `0.5px solid ${exploreCategory === cat ? T.dark : T.rule}`,
                      color: exploreCategory === cat ? '#FFFFFF' : T.muted,
                      fontFamily: T.mono, fontSize: 9, fontWeight: exploreCategory === cat ? 600 : 400,
                      cursor: 'pointer', borderRadius: 3, transition: 'all .12s',
                    }}
                  >
                    {cat}
                  </button>
                ))}
              </div>
            </div>

            {/* Niveau */}
            {categoryHasLevels(exploreCategory) && (
              <div style={{ flex: '1 1 200px' }}>
                <LevelSelector
                  category={exploreCategory}
                  value={exploreLevel}
                  onChange={handleExploreLevel}
                  hint={false}
                  size="compact"
                />
              </div>
            )}
          </div>

          {/* Indicateur vue exploration */}
          {isExploring && !isEditingSegment && (
            <div style={{ marginTop: 14, paddingTop: 12, borderTop: `1px solid ${T.rule}`, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <span style={{ fontFamily: T.mono, fontSize: 9, color: T.muted, letterSpacing: '.04em' }}>
                Vue exploration —{' '}
                <span style={{ color: T.ink }}>
                  segment déclaré : <strong>{declaredCategory}{declaredLevel ? ` ${declaredLevel}` : ''}</strong>
                </span>
              </span>
              <button
                onClick={handleResetExplore}
                style={{
                  padding: '4px 10px', background: 'transparent',
                  border: `0.5px solid ${T.rule}`, color: T.muted,
                  fontFamily: T.mono, fontSize: 8, fontWeight: 500,
                  cursor: 'pointer', borderRadius: 3,
                }}
              >
                Revenir à mon segment
              </button>
            </div>
          )}

          {/* Modifier le segment déclaré */}
          <div style={{ marginTop: 14, paddingTop: 12, borderTop: `1px solid ${T.rule}` }}>
            {!isEditingSegment ? (
              <button
                onClick={handleOpenEdit}
                style={{
                  background: 'transparent', border: 'none', padding: 0,
                  fontFamily: T.mono, fontSize: 9, letterSpacing: '.08em',
                  color: T.muted, cursor: 'pointer', textDecoration: 'underline',
                  textUnderlineOffset: 3,
                }}
              >
                Modifier mon segment déclaré ({declaredCategory}{declaredLevel ? ` ${declaredLevel}` : ''})
              </button>
            ) : (
              <div>
                <div style={{ fontFamily: T.mono, fontSize: 9, letterSpacing: '.16em', textTransform: 'uppercase', color: T.gold, marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ width: 10, height: 1, background: T.gold }} />
                  Corriger mon segment
                </div>
                <div style={{ marginBottom: 14 }}>
                  <div style={{ fontFamily: T.mono, fontSize: 8, letterSpacing: '.14em', textTransform: 'uppercase', color: T.muted, marginBottom: 8 }}>
                    Catégorie
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {categories.map(cat => (
                      <button
                        key={cat}
                        type="button"
                        onClick={() => { setEditCategory(cat); setEditLevel(null) }}
                        style={{
                          padding: '8px 10px',
                          background: editCategory === cat ? T.gold : T.surface,
                          border: `1px solid ${editCategory === cat ? T.gold : T.rule}`,
                          color: editCategory === cat ? T.ink : T.muted,
                          fontFamily: T.mono, fontSize: 9, letterSpacing: '.06em',
                          cursor: 'pointer', fontWeight: editCategory === cat ? 700 : 400,
                          transition: 'all .12s',
                        }}
                      >
                        {cat}
                      </button>
                    ))}
                  </div>
                </div>
                {editNeedsLevel && (
                  <div style={{ marginBottom: 14 }}>
                    <LevelSelector
                      category={editCategory}
                      value={editLevel}
                      onChange={setEditLevel}
                      hint={false}
                      size="compact"
                    />
                  </div>
                )}
                {editError && (
                  <div style={{ marginBottom: 12, padding: '8px 12px', background: 'rgba(220,38,38,0.05)', border: '1px solid rgba(220,38,38,0.2)', display: 'flex', alignItems: 'center', gap: 8 }}>
                    <AlertCircle size={13} color="#ef4444" />
                    <span style={{ fontFamily: T.mono, fontSize: 9, color: '#dc2626' }}>{editError}</span>
                  </div>
                )}
                <div style={{ display: 'flex', gap: 10 }}>
                  <button
                    onClick={handleSubmitEdit}
                    disabled={!canEditSubmit || submittingEdit}
                    style={{
                      padding: '9px 18px',
                      background: canEditSubmit ? T.gold : T.rule,
                      color: canEditSubmit ? T.dark : T.muted,
                      fontFamily: T.mono, fontSize: 9, letterSpacing: '.12em', textTransform: 'uppercase', fontWeight: 700,
                      border: 'none', cursor: canEditSubmit && !submittingEdit ? 'pointer' : 'not-allowed',
                    }}
                  >
                    {submittingEdit ? 'Enregistrement…' : 'Enregistrer'}
                  </button>
                  <button
                    onClick={() => { setIsEditingSegment(false); setEditError(null) }}
                    style={{
                      padding: '9px 18px',
                      background: 'transparent',
                      color: T.muted,
                      fontFamily: T.mono, fontSize: 9, letterSpacing: '.12em', textTransform: 'uppercase',
                      border: `1px solid ${T.rule}`, cursor: 'pointer',
                    }}
                  >
                    Annuler
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ═══ Plan FREE ═══ */}
      {isFree ? (
        <FreeLockedState isMobile={isMobile} />
      ) : loading ? (
        <div style={{ textAlign: 'center', padding: '80px 0' }}>
          <div style={{ width: 26, height: 26, border: `2px solid ${T.goldBdr}`, borderTopColor: T.gold, borderRadius: '50%', animation: 'spin .7s linear infinite', margin: '0 auto' }} />
        </div>
      ) : error ? (
        <div style={{ background: T.surface, border: `1px solid ${T.rule}`, padding: '40px 24px', textAlign: 'center' }}>
          <AlertCircle size={28} color="#ef4444" style={{ marginBottom: 12 }} />
          <p style={{ fontFamily: T.mono, fontSize: 11, color: T.ink, marginBottom: 16 }}>{error}</p>
          <button onClick={loadBenchmark} style={{
            padding: '10px 20px', background: T.gold, color: T.dark,
            fontFamily: T.mono, fontSize: 9, letterSpacing: '.14em', textTransform: 'uppercase', fontWeight: 700,
            border: 'none', cursor: 'pointer',
          }}>
            Réessayer
          </button>
        </div>
      ) : data?.reason === 'no_segment_declared' ? (
        <NoSegmentState
          isMobile={isMobile}
          onSubmit={handleSubmitSegment}
          submitting={submittingSegment}
          error={submitError}
        />
      ) : data?.unlocked === false ? (
        <NotEnoughClubsState
          data={data}
          isMobile={isMobile}
          onShareClick={() => setShowShareModal(true)}
        />
      ) : data?.unlocked === true ? (
        <UnlockedState
          data={data}
          isMobile={isMobile}
          onShareClick={() => setShowShareModal(true)}
        />
      ) : null}

      {/* Modale Partager */}
      <ShareModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        segmentLabel={data?.segment ? `${data.segment.category} ${SENIOR_LEVEL_LABELS[data.segment.level] ?? data.segment.level}` : null}
      />
    </DashboardLayout>
  )
}
